// ── Text helpers ─────────────────────────────────────────────────────────────

export const getIncentiveSearchText = (incentive) => {
     return [
          incentive?.name,
          incentive?.description,
          incentive?.type,
          incentive?.supportFor,
          incentive?.support_for,
          incentive?.support_subtype,
          incentive?.incentive_focus
     ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
};

export const getSupportForText = (incentive) => {
     return String(incentive?.supportFor || incentive?.support_for || "").toLowerCase();
};

// ── Eligibility ───────────────────────────────────────────────────────────────

export const isEligibleIncentive = (incentive) => {
     const eligibility = String(
          incentive?.evaluation?.eligibility || incentive?.eligibility || incentive?.evaluation?.status || ""
     ).toLowerCase();
     if (!eligibility) return true;
     return eligibility === "eligible";
};

// ── Grantor / source predicates ───────────────────────────────────────────────

export const isUberGoElectricIncentive = (incentive) => {
     const normalizedName = String(incentive?.name || "")
          .trim()
          .toLowerCase();
     const normalizedGrantor = String(incentive?.grantor || "")
          .trim()
          .toLowerCase();

     return (
          normalizedName === "go electric incentive" ||
          normalizedName === "uber go electric incentive" ||
          (normalizedGrantor === "uber" && normalizedName.includes("go electric"))
     );
};

export const isStateLikeIncentive = (incentive) => {
     const grantorType = String(
          incentive?.grantor_type ||
               incentive?.grantorType ||
               incentive?.grantor_type_name ||
               incentive?.grantorTypeName ||
               ""
     ).toLowerCase();

     const grantor = String(incentive?.grantor || "").toLowerCase();
     const text = getIncentiveSearchText(incentive);

     return (
          grantorType.includes("state") ||
          grantorType.includes("province") ||
          grantorType.includes("air district") ||
          grantorType.includes("regional") ||
          grantorType.includes("county") ||
          grantorType.includes("municipal") ||
          grantorType.includes("miscellaneous") ||
          grantor.includes("state") ||
          grantor.includes("air district") ||
          grantor.includes("county") ||
          grantor.includes("south coast") ||
          grantor.includes("ladwp") ||
          grantor.includes("california") ||
          text.includes("clean cars 4 all") ||
          text.includes("clean cars for all") ||
          text.includes("driving clean assistance") ||
          text.includes("replace your ride") ||
          text.includes("vehicle retirement consumer assistance")
     );
};

// ── Incentive-type predicates ─────────────────────────────────────────────────

export const isChargingIncentive = (incentive) => {
     const supportFor = getSupportForText(incentive);
     const text = getIncentiveSearchText(incentive);

     if (supportFor === "vehicle purchase" || supportFor === "used vehicle purchase") {
          return false;
     }

     if (supportFor === "charging station" || supportFor === "electricity") {
          return true;
     }

     if (text.includes("toll discount") || text.includes("e-zpass") || text.includes("ezpass")) {
          return false;
     }

     return (
          text.includes("charg") ||
          text.includes("evse") ||
          text.includes("charging station") ||
          text.includes("charging equipment") ||
          text.includes("charging infrastructure") ||
          text.includes("chargepoint") ||
          text.includes("wallbox") ||
          text.includes("refueling") ||
          text.includes("fueling infrastructure") ||
          text.includes("fueling property") ||
          text.includes("make-ready") ||
          text.includes("make ready") ||
          text.includes("off-peak") ||
          text.includes("kilowatt-hour") ||
          text.includes("kwh") ||
          text.includes("tou rate")
     );
};

export const isVehicleRetirementIncentive = (incentive) => {
     const text = `${incentive?.name || ""} ${incentive?.description || ""} ${incentive?.type || ""}`.toLowerCase();

     return (
          text.includes("vehicle retirement") ||
          text.includes("old car buy back") ||
          text.includes("buy back") ||
          text.includes("scrap program") ||
          text.includes("scrappage") ||
          text.includes("retire")
     );
};

// ── Categorization ────────────────────────────────────────────────────────────

export const categorizeIncentiveForCatalog = (incentive) => {
     const text = [
          incentive?.name,
          incentive?.description,
          incentive?.type,
          incentive?.supportFor,
          incentive?.support_for
     ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

     const nameDesc = `${incentive?.name || ""} ${incentive?.description || ""}`.toLowerCase();
     const categories = [];

     const support = getSupportForText(incentive);

     if (support === "charging station" || support === "electricity") {
          return ["charging"];
     }

     const isChargerProgram =
          /\brecharg/.test(nameDesc) ||
          /\bcharging (station|equipment|infrastructure|property|port|rate|rates)\b/.test(nameDesc) ||
          /\bevse\b/.test(nameDesc) ||
          /\bchargepoint\b/.test(nameDesc) ||
          /\bwallbox\b/.test(nameDesc) ||
          /\brefueling\b/.test(nameDesc) ||
          /\b(alternative )?fueling (infrastructure|property|equipment)\b/.test(nameDesc) ||
          /\bcharger(s)?\b/.test(nameDesc) ||
          /\binstall(ing|ation)?\b.*\b(charg|evse|station)/.test(nameDesc) ||
          /\bproperty credit\b/.test(nameDesc);

     const isCharging =
          isChargerProgram ||
          /\bcharg/.test(text) ||
          text.includes("evse") ||
          text.includes("electric rate") ||
          text.includes("kwh") ||
          text.includes("chargepoint") ||
          text.includes("wallbox") ||
          text.includes("refueling");

     if (isCharging) {
          categories.push("charging");
     }

     if (isChargerProgram) {
          return categories;
     }

     const isUsed =
          /\bused\b[\w\s-]{0,40}\b(ev|evs|electric|vehicle|vehicles|car|cars|truck|trucks|zev|zevs|nzev|nzevs|zero[- ]emission|near[- ]zero)\b/.test(
               text
          ) ||
          /\bpurchased? a used\b/.test(text) ||
          /\bpre[- ]?owned\b/.test(text) ||
          /\bsecond[- ]?hand\b/.test(text) ||
          /\bpreviously owned\b/.test(text) ||
          support === "used vehicle purchase";

     if (isUsed) {
          categories.push("used");
     }

     const mentionsBoth =
          text.includes("new or used") ||
          text.includes("used or new") ||
          text.includes("new and used") ||
          text.includes("used and new");

     const isNew =
          text.includes("new") ||
          text.includes("purchase") ||
          text.includes("lease") ||
          text.includes("buy") ||
          text.includes("vehicle retirement") ||
          text.includes("replace your") ||
          text.includes("clean vehicle") ||
          text.includes("ev rebate") ||
          text.includes("electric vehicle") ||
          !isCharging;

     if (isNew && !categories.includes("new") && (!isUsed || mentionsBoth)) {
          categories.push("new");
     }

     if (categories.length === 0) {
          categories.push("new");
     }

     return categories;
};

export const categorizeIncentiveForHero = (incentive) => {
     const supportFor = getSupportForText(incentive);
     const text = getIncentiveSearchText(incentive);
     const categories = [];

     // Vehicle purchase should win before any "charging" keyword checks.
     // This fixes Charge Up / Charge Up+ NJ being excluded from hero totals.
     if (supportFor === "vehicle purchase" || supportFor === "used vehicle purchase") {
          if (supportFor === "used vehicle purchase" || incentive?.applicable_to_used_vehicles === true) {
               categories.push("used");
          }

          if (supportFor === "vehicle purchase" || incentive?.applicable_to_new_vehicles === true) {
               categories.push("new");
          }

          return categories.length ? categories : ["new"];
     }

     if (supportFor === "charging station" || supportFor === "electricity") {
          return ["charging"];
     }

     const isCharging =
          /\bcharg/.test(text) ||
          text.includes("evse") ||
          text.includes("electric rate") ||
          text.includes("kwh") ||
          text.includes("chargepoint") ||
          text.includes("wallbox") ||
          text.includes("refueling");

     if (isCharging) {
          categories.push("charging");
     }

     const isUsed =
          supportFor === "used vehicle purchase" ||
          incentive?.applicable_to_used_vehicles === true ||
          /\bused\b[\w\s-]{0,40}\b(ev|evs|electric|vehicle|vehicles|car|cars|truck|trucks|zev|zevs|nzev|nzevs|zero[- ]emission|near[- ]zero)\b/.test(
               text
          ) ||
          /\bpurchased? a used\b/.test(text) ||
          /\bpre[- ]?owned\b/.test(text) ||
          /\bsecond[- ]?hand\b/.test(text) ||
          /\bpreviously owned\b/.test(text);

     const mentionsBoth =
          text.includes("new or used") ||
          text.includes("used or new") ||
          text.includes("new and used") ||
          text.includes("used and new");

     const isNew =
          supportFor === "vehicle purchase" ||
          incentive?.applicable_to_new_vehicles === true ||
          text.includes("new") ||
          text.includes("purchase") ||
          text.includes("lease") ||
          text.includes("buy") ||
          text.includes("vehicle retirement") ||
          text.includes("replace your") ||
          text.includes("clean vehicle") ||
          text.includes("ev rebate") ||
          text.includes("electric vehicle") ||
          !isCharging;

     if (isUsed) {
          categories.push("used");
     }

     if (isNew && (!isUsed || mentionsBoth)) {
          categories.push("new");
     }

     if (categories.length === 0) {
          categories.push("new");
     }

     return categories;
};

export const isExcludedPassengerEvIncentive = (incentive) => {
     const text = `${incentive?.name || ""} ${incentive?.description || ""}`.toLowerCase();

     // Hide incentives that aren't true passenger-EV purchase incentives:
     // - CA Zero-Emission Assurance Project (ZAP): battery warranty assurance program
     // - Colorado Innovative Truck Credit: applies to heavy trucks (GVWR > 8,500 lbs)
     return (
          text.includes("zero-emission assurance project") ||
          text.includes("zero emission assurance project") ||
          text.includes("innovative truck credit")
     );
};
